import type { PbCarouselClientSettings } from "../types";

const LABELS = { previous: "Previous", next: "Next", slide: "Slide" };

/**
 * Adds arrows, dots and autoplay to carousels the parser built from slick
 * configs in `text/x-magento-init` (`[data-pb-carousel]`, see
 * `parser/magentoInit.ts`). Layout and slides per view are pure CSS
 * (scroll-snap track, `--pb-carousel-per-view` / `--pb-carousel-scroll`), so
 * a carousel with all three options off is left untouched. Controls reuse the
 * Page Builder slider styles. Returns a cleanup that restores the markup.
 */
export function enhanceMagentoCarousels(root: HTMLElement): () => void {
  const cleanups: Array<() => void> = [];
  root.querySelectorAll<HTMLElement>("[data-pb-carousel]").forEach((track) => {
    const settings = readSettings(track);
    if (!settings || !(settings.arrows || settings.dots || settings.autoplay)) return;
    cleanups.push(enhance(track, settings));
  });
  return () => cleanups.forEach((cleanup) => cleanup());
}

function readSettings(track: HTMLElement): PbCarouselClientSettings | null {
  try {
    return JSON.parse(track.dataset.pbCarousel ?? "") as PbCarouselClientSettings;
  } catch {
    return null;
  }
}

function cssCount(el: HTMLElement, property: string): number {
  const n = Number.parseInt(getComputedStyle(el).getPropertyValue(property), 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function reducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function button(className: string, label: string, onClick: () => void): HTMLButtonElement {
  const el = document.createElement("button");
  el.type = "button";
  if (className) el.className = className;
  el.setAttribute("aria-label", label);
  el.addEventListener("click", onClick);
  return el;
}

function enhance(track: HTMLElement, settings: PbCarouselClientSettings): () => void {
  const slides = () => Array.from(track.children) as HTMLElement[];
  const perView = () => cssCount(track, "--pb-carousel-per-view");
  const step = () => cssCount(track, "--pb-carousel-scroll");
  const maxIndex = () => Math.max(0, slides().length - perView());
  const current = () => {
    const width = slides()[0]?.offsetWidth || 1;
    return Math.round(track.scrollLeft / width);
  };

  const goTo = (index: number) => {
    const max = maxIndex();
    let target = index;
    // `infinite` rewinds at either end (slick clones slides instead).
    if (target > max) target = settings.infinite && current() >= max ? 0 : max;
    if (target < 0) target = settings.infinite && current() <= 0 ? max : 0;
    const list = slides();
    if (!list[target] || !list[0]) return;
    track.scrollTo({ left: list[target].offsetLeft - list[0].offsetLeft, behavior: reducedMotion() ? "auto" : "smooth" });
  };

  const frame = document.createElement("div");
  frame.className = "pb-carousel-frame";
  track.parentNode?.insertBefore(frame, track);
  frame.appendChild(track);

  const prev = settings.arrows
    ? button("pb-slider__arrow pb-slider__arrow--prev", LABELS.previous, () => goTo(current() - step()))
    : null;
  const next = settings.arrows ? button("pb-slider__arrow pb-slider__arrow--next", LABELS.next, () => goTo(current() + step())) : null;
  if (prev && next) frame.append(prev, next);

  const dots = settings.dots ? document.createElement("ul") : null;
  if (dots) {
    dots.className = "pb-slider__dots";
    frame.append(dots);
  }

  const sync = () => {
    const index = current();
    const max = maxIndex();
    if (prev && next) {
      prev.hidden = next.hidden = max === 0;
      prev.disabled = !settings.infinite && index <= 0;
      next.disabled = !settings.infinite && index >= max;
    }
    if (dots) {
      const pages = Math.ceil(max / step()) + 1;
      if (dots.childElementCount !== pages) {
        dots.replaceChildren(
          ...Array.from({ length: pages }, (_, page) => {
            const li = document.createElement("li");
            li.append(button("", `${LABELS.slide} ${page + 1}`, () => goTo(page * step())));
            return li;
          })
        );
      }
      dots.hidden = max === 0;
      const active = index >= max ? pages - 1 : Math.floor(index / step());
      Array.from(dots.children).forEach((li, page) => {
        li.classList.toggle("is-active", page === active);
        li.firstElementChild?.setAttribute("aria-current", page === active ? "true" : "false");
      });
    }
  };

  let frameRequest = 0;
  const onScroll = () => {
    cancelAnimationFrame(frameRequest);
    frameRequest = requestAnimationFrame(sync);
  };

  let paused = false;
  const pause = () => {
    paused = true;
  };
  const resume = () => {
    paused = false;
  };
  const timer =
    settings.autoplay && !reducedMotion()
      ? window.setInterval(() => {
          if (!paused && !document.hidden) goTo(current() + step());
        }, settings.autoplaySpeed)
      : undefined;

  sync();
  track.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });
  frame.addEventListener("mouseenter", pause);
  frame.addEventListener("mouseleave", resume);
  frame.addEventListener("focusin", pause);
  frame.addEventListener("focusout", resume);

  return () => {
    window.clearInterval(timer);
    cancelAnimationFrame(frameRequest);
    track.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", onScroll);
    frame.parentNode?.insertBefore(track, frame);
    frame.remove();
  };
}
